<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("rappels.grid.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearRappel()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="FechaInicio" :caption="$t('rappels.grid.fecha_inicio')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaFin" :caption="$t('rappels.grid.fecha_fin')" data-type="date" format="dd/MM/yyyy">
      </DxColumn>
      <DxColumn data-field="Nombre" :caption="$t('rappels.grid.nombre')"></DxColumn>
      <DxColumn data-field="TipoElemento" :caption="$t('rappels.grid.tipo_elemento')">
        <DxLookup column-auto-width="true" v-bind="getLookupTipoElemento()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TipoSuministro" :caption="$t('rappels.grid.tipo_suministro')">
        <DxLookup column-auto-width="true" v-bind="getLookupTipoEnergia()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="VolumenMin" :caption="$t('rappels.grid.volumen_min')" data-type="number">
      </DxColumn>
      <DxColumn data-field="Importe" :caption="$t('rappels.grid.importe')" data-type="number"
        cell-template="importeTemplate">
      </DxColumn>
      <DxColumn data-field="PotenciaMax" :caption="$t('rappels.grid.potencia_max')" data-type="number"
        cell-template="potenciaTemplate">
      </DxColumn>
      <DxColumn data-field="EmpresaAgentes" :caption="$t('rappels.grid.empresa_agentes')">
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('rappels.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('rappels.grid.modificar'),
          icon: 'fa-regular fa-edit text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('rappels.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id)
        }
      ]" />

      <template #importeTemplate="{ data }">
        <span>{{ data.value }} €</span>
      </template>
      <template #potenciaTemplate="{ data }">
        <span v-if="data.value">{{ data.value }} kW</span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import type { ColumnLookup } from "devextreme/ui/data_grid";
import { type DXLoadOptions } from "@api/api";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import RappelEnergiaForm from "./RappelEnergiaForm.vue";
import { CreateRappelEnergia } from "@/components/rappels-energia/models/CreateRappelEnergia";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const gridProperties = ref<DxDataGrid>();

const props = defineProps({
  idEmpresaServicios: String
});

onMounted(async () => {
  gridProperties.value = getDefaultGridProperties();
  setDatasource();
});

function getLookupTipoEnergia(): ColumnLookup {
  return getLookup('comisiones', 'tipo_suministro');
};

function getLookupTipoElemento(): ColumnLookup {
  return getLookup('comisiones', 'tipo_elemento');
};

async function crearRappel() {
  let title = `${intl.i18n.t("rappel_energia.creacion.titulo")}`;
  var detalleEmpresaServicios = await api.callStandardApi("getEmpresaServiciosEnergiaById", { showLoading: false }, props.idEmpresaServicios!);
  let nombreEmpresaServicios = detalleEmpresaServicios.Nombre;

  modal.showModal(
    RappelEnergiaForm,
    { model: new CreateRappelEnergia({ IdEmpresaServicios: props.idEmpresaServicios, EmpresaServicios: nombreEmpresaServicios }), isEditMode: false },
    { title: title, width: 900, height: 450 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const setDatasource = async () => {

  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      if (q.filter != null && q.filter.length >= 0)
        q.filter = [["IdEmpresaServicios", "=", props.idEmpresaServicios], "and", q.filter];
      else
        q.filter = ["IdEmpresaServicios", "=", props.idEmpresaServicios];

      return await api.callStandardApi("getRappelsEnergiaForGrid", { showLoading: false }, q)
    }
  });
}

const verDetalle = async (idRappel: string) => {
  let title = `${intl.i18n.t("rappel_energia.edicion.titulo")}`;
  await api.callStandardApi("getRappelEnergiaById", { showLoading: true }, idRappel).then(value => {
    modal.showModal(
      RappelEnergiaForm,
      { model: new CreateRappelEnergia().createFromRappelDetail(value), isEditMode: true },
      { title: title, width: 900, height: 450 }
    ).then((value) => {
      if (value == messageBoxResult.ok)
        grid.value?.instance?.refresh();
    });
  });
}

const borrar = async (idRappel: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t('rappels.grid.borrar_pregunta'), intl.i18n.t('rappels.grid.titulo_confirma_borrar'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteRappelEnergia", {}, idRappel)
      .then(() => {
        toastSuccess(intl.i18n.t('rappels.borrado_exito'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('rappel.borrado_fallo')));
}

</script>

<style lang="scss" scoped></style>